import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/shared/ui/Button'
import { Field } from '@/shared/ui/Field'
import { Modal } from '@/shared/ui/Modal'
import { Qr } from '@/shared/ui/Qr'
import { LockIcon, MailIcon, ShieldIcon } from '@/shared/ui/icons'
import { useAuth, useCabinet, useStores } from '@/stores/RootStore'
import styles from './Settings.module.css'

/**
 * Диалоги аккаунта: почта, пароль, второй фактор.
 *
 * Живут на уровне оболочки кабинета, а не на странице: в макете
 * отдельной страницы настроек нет вовсе — всё открывается из меню
 * профиля, которое доступно с любого экрана. Смонтированы один раз,
 * иначе диалог закрывался бы вместе с попапом меню, из которого его
 * открыли.
 */
export const AccountDialogs = observer(function AccountDialogs() {
  return (
    <>
      <EmailDialogs />
      <PasswordDialogs />
      <TwoFactorDialogs />
    </>
  )
})

const EmailDialogs = observer(function EmailDialogs() {
  const { settings: store } = useStores()
  const leave = useRelogin()

  if (store.emailStep === 'idle') return null

  if (store.emailStep === 'form') {
    return (
      <Modal title="Изменить почту" onClose={() => store.closeEmail()}
             actions={
               <>
                 <Button variant="secondary" onClick={() => store.closeEmail()}>Отмена</Button>
                 <Button variant="primary" busy={store.emailBusy}
                         onClick={() => void store.requestEmail()}>Изменить</Button>
               </>
             }>
        {/* Одно поле — как в макете (Change Email 4:14841, карточка 608x260).
            Пароль и код 2FA здесь не спрашиваются: код всё равно уходит
            на текущий адрес, и владелец ящика — это и есть проверка. */}
        <Field placeholder="Новая почта" type="email" autoComplete="off"
               icon={<MailIcon size={24} />} value={store.newEmail}
               onChange={(e) => store.patchEmail({ newEmail: e.target.value })}
               error={store.emailErrors.email ?? null} />
      </Modal>
    )
  }

  return (
    <Modal title="Подтверждение"
           note={`Введите код, отправленный на почту ${maskEmail(store.currentEmail)}`}
           onClose={() => store.closeEmail()}
           actions={
             <>
               <Button variant="secondary" onClick={() => store.closeEmail()}>Отмена</Button>
               <Button variant="primary" busy={store.emailBusy}
                       onClick={() => void store.confirmEmail().then((done) => {
                         if (done) leave()
                       })}>
                 Подтвердить
               </Button>
             </>
           }>
      <Field placeholder="Код подтверждения" inputMode="numeric" maxLength={6}
             icon={<ShieldIcon size={24} />} value={store.emailCode}
             onChange={(e) => store.patchEmail({ emailCode: e.target.value })}
             error={store.emailErrors.code ?? null} />
    </Modal>
  )
})

const PasswordDialogs = observer(function PasswordDialogs() {
  const { settings: store } = useStores()
  const leave = useRelogin()

  if (store.passwordStep === 'idle') return null

  if (store.passwordStep === 'form') {
    return (
      <Modal title="Изменить пароль" onClose={() => store.closePassword()}
             actions={
               <>
                 <Button variant="secondary" onClick={() => store.closePassword()}>Отмена</Button>
                 <Button variant="primary" busy={store.passwordBusy}
                         onClick={() => void store.requestPassword()}>Изменить</Button>
               </>
             }>
        {/* Текущий пароль не спрашивается — так в макете (Change Password
            4:14633: два поля, 608x324). Подтверждение впереди: код из
            приложения или письмо на текущую почту. */}
        <div className={styles.pair}>
          <Field placeholder="Новый пароль" type="password" autoComplete="new-password"
                 icon={<LockIcon size={24} />} value={store.next}
                 onChange={(e) => store.patchPassword({ next: e.target.value })}
                 error={store.passwordErrors.next ?? null} />

          <Field placeholder="Подтвердите пароль" type="password" autoComplete="new-password"
                 icon={<LockIcon size={24} />} value={store.repeat}
                 onChange={(e) => store.patchPassword({ repeat: e.target.value })}
                 error={store.passwordErrors.repeat ?? null} />
        </div>
      </Modal>
    )
  }

  // Текст подсказки решает бекенд, а не включённость 2FA в профиле:
  // разойдясь, экран отправит человека искать письмо, которого нет.
  const note = store.passwordMethod === 'totp'
    ? 'Введите код из приложения двухфакторной аутентификации'
    : `Введите код, отправленный на почту ${maskEmail(store.currentEmail)}`

  return (
    <Modal title="Подтверждение" note={note} onClose={() => store.closePassword()}
           actions={
             <>
               <Button variant="secondary" onClick={() => store.closePassword()}>Отмена</Button>
               <Button variant="primary" busy={store.passwordBusy}
                       onClick={() => void store.confirmPassword().then((done) => {
                         if (done) leave()
                       })}>
                 Подтвердить
               </Button>
             </>
           }>
      <Field placeholder="Код подтверждения" inputMode="numeric" maxLength={8}
             icon={<ShieldIcon size={24} />} value={store.passwordCode}
             onChange={(e) => store.patchPassword({ passwordCode: e.target.value })}
             error={store.passwordErrors.code ?? null} />
    </Modal>
  )
})

const TwoFactorDialogs = observer(function TwoFactorDialogs() {
  const { settings: store } = useStores()
  const step = store.twoFactor

  if (step.kind === 'idle') return null

  if (step.kind === 'scan') {
    return (
      <Modal title="Настроить 2FA"
             note="Для защиты аккаунта подключите приложение-аутентификатор."
             onClose={() => store.closeTwoFactor()}
             actions={
               <>
                 <Button variant="secondary" onClick={() => store.closeTwoFactor()}>Отмена</Button>
                 <Button variant="primary" busy={store.twoFactorBusy}
                         onClick={() => void store.enableTwoFactor()}>Включить 2FA</Button>
               </>
             }>
        {/* По макету: подпись «QR-код», поле-подложка с кодом внутри
            (544x360 на десктопе, QR 312), под ним пояснение. Размер
            кода задан в CSS по ширине экрана; canvas рисуется в самом
            крупном и масштабируется вниз — так он остаётся резким. */}
        <div className={styles.qrBlock}>
          <span className={styles.qrLabel}>QR-код</span>
          <div className={styles.qrField}>
            <div className={styles.qrCanvas}><Qr value={step.uri} size={312} /></div>
          </div>
          <span className={styles.qrNote}>
            Отсканируйте QR-код через приложение-аутентификатор и введите код подтверждения.
          </span>
        </div>

        {/* Ключ показывается текстом: телефон может не иметь камеры,
            а без ключа подключиться будет нечем. */}
        <Field label="Ключ восстановления" value={groupKey(step.secret)} readOnly />

        <Field label="Код 2FA" placeholder="Введите код из приложения"
               inputMode="numeric" maxLength={8} value={store.code}
               onChange={(e) => store.setCode(e.target.value)}
               error={store.codeError} />
      </Modal>
    )
  }

  if (step.kind === 'codes') {
    return (
      <Modal title="Резервные коды"
             note="Эти коды помогут восстановить доступ, если вы потеряете устройство."
             onClose={() => store.closeTwoFactor()}
             actions={
               <>
                 <Button variant="secondary" onClick={() => store.downloadCodes()}>Скачать коды</Button>
                 <Button variant="primary" disabled={!store.codesSaved}
                         onClick={() => { store.confirmCodesSaved(); store.closeTwoFactor() }}>
                   Я сохранил коды
                 </Button>
               </>
             }>
        {/* Показываются один раз и только здесь. Закрыть окно, не
            подтвердив сохранение, нельзя: потерянный телефон без
            этих кодов означает потерянный аккаунт вместе с балансом. */}
        <ul className={styles.codes}>
          {step.codes.map((code) => <li key={code} className={styles.code}>{code}</li>)}
        </ul>

        <label className={styles.check}>
          <input type="checkbox" checked={store.codesSaved}
                 onChange={() => store.confirmCodesSaved()} />
          <span>Я сохранил коды в надёжном месте</span>
        </label>
      </Modal>
    )
  }

  return (
    <Modal title="Отключить 2FA?"
           note="Введите код из приложения двухфакторной аутентификации"
           onClose={() => store.closeTwoFactor()}
           actions={
             <>
               <Button variant="secondary" onClick={() => store.closeTwoFactor()}>Отменить</Button>
               <Button variant="primary" busy={store.twoFactorBusy}
                       onClick={() => void store.disableTwoFactor()}>Отключить 2FA</Button>
             </>
           }>
      <Field label="Код подтверждения" placeholder="Код из приложения или резервный"
             inputMode="text" maxLength={9} value={store.code}
             onChange={(e) => store.setCode(e.target.value)}
             error={store.codeError} />
    </Modal>
  )
})

/**
 * Уйти на вход после смены почты или пароля.
 *
 * И то и другое гасит все выданные токены — текущая сессия мертва
 * в ту же секунду. Кабинет чистим, иначе следующий вошедший увидит
 * чужие балансы, пока не догрузятся его собственные.
 */
function useRelogin(): () => void {
  const auth = useAuth()
  const cabinet = useCabinet()
  const navigate = useNavigate()
  return () => { cabinet.reset(); auth.forget(); navigate('/login') }
}

/** «n***w@example.com» — показать адрес, не выдав его целиком. */
export function maskEmail(email: string): string {
  const [name = '', domain = ''] = email.split('@')
  if (!domain) return email
  const head = name.slice(0, 1)
  const tail = name.length > 1 ? name.slice(-1) : ''
  return `${head}***${tail}@${domain}`
}

/** Ключ по четыре символа: так его переписывают без ошибок. */
function groupKey(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim()
}
